// «Venta rápida» del portal del vendedor: todo lo que no es teléfono, con la tabla a todo lo ancho y
// el carrito en un cajón. Va sin cliente; cobrar exige el detalle del cobro. Al cerrar muestra el folio.
import { useMemo, useReducer, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/kit';
import { ScreenFrame } from '@/shared/patterns/ScreenFrame';
import { useCatalog } from '@/domains/sales/wizard/api/useWizard';
import { AccessoriesTable } from './accessories/components/AccessoriesTable';
import { AccessoriesCartBar } from './accessories/components/AccessoriesCartBar';
import { AccessoriesCartDrawer } from './accessories/components/AccessoriesCartDrawer';
import { isLastAccessories, quickSaleItemsOf } from './accessories/lib/accessoriesCatalog';
import { maxQtyFor } from '@/domains/sales/wizard/cartLines';
import { PosFiltersPanel } from './pos/components/PosFiltersPanel';
import { PosShell } from './pos/components/PosShell';
import { usePosExplorer } from './pos/usePosExplorer';
import { cartReducer, summarizeCart } from './accessories/lib/accessoriesCart';
import {
    buildAccessorySaleRequest,
    checkoutBlocker,
    identifiersComplete,
    type CartIdentifiers,
    idempotencySlotFor,
    type IdempotencySlot,
} from './accessories/lib/accessoriesCheckout';
import { saleFailure, type SaleFailure } from './accessories/lib/accessorySaleErrors';
import { useCreateAccessorySale } from './accessories/api/useAccessorySale';
import { AccessorySaleReceiptModal } from './accessories/components/AccessorySaleReceiptModal';
import type { AccessorySale } from '@/api/services/retail-wizard/accessorySales.client';
import { initialPaymentDrafts, type PaymentDraft } from '@/domains/sales/wizard/salePayments';

export function SalesAccessories() {
    // El catálogo de la tienda es de solo lectura; el silo ignora el `sessionId`.
    const catalog = useCatalog('portal');
    const [cart, dispatch] = useReducer(cartReducer, {});
    const [payments, setPayments] = useState<readonly PaymentDraft[]>(initialPaymentDrafts);
    const [identifiers, setIdentifiers] = useState<CartIdentifiers>({});
    const [cartOpen, setCartOpen] = useState(false);
    const [confirmingClear, setConfirmingClear] = useState(false);
    const [saleError, setSaleError] = useState<string | null>(null);
    const [receipt, setReceipt] = useState<AccessorySale | null>(null);
    const idempotency = useRef<IdempotencySlot | null>(null);
    const createSale = useCreateAccessorySale();

    const accessories = useMemo(() => quickSaleItemsOf(catalog.data?.items ?? []), [catalog.data]);
    const pos = usePosExplorer(accessories, isLastAccessories);
    const summary = useMemo(() => summarizeCart(cart, accessories), [cart, accessories]);

    /** Suma o resta unidades sin pasar del stock de la tienda (un servicio, de su tope). */
    const changeQty = (sku: string, delta: number): void => {
        const item = accessories.find((candidate) => candidate.sku === sku);
        if (delta > 0 && (!item || (cart[sku] ?? 0) + delta > maxQtyFor(item))) return;
        dispatch({ type: 'change', sku, delta });
    };

    const blocker = checkoutBlocker({
        unitsCount: summary.unitsCount,
        identifiersComplete: identifiersComplete(summary.lines, identifiers),
        totalCents: summary.subtotalCents,
        payments,
    });

    const refreshCatalog = (): void => {
        void catalog.refetch();
    };

    /** Aplica la acción que el contrato pide para cada rechazo de la venta. */
    const recoverFrom = (failure: SaleFailure): void => {
        switch (failure.recovery) {
            case 'remove-line':
                if (failure.sku) dispatch({ type: 'remove', sku: failure.sku });
                refreshCatalog();
                break;
            case 'reload':
                refreshCatalog();
                break;
            case 'retry':
            case 'none':
                break;
        }
    };

    const charge = async (): Promise<void> => {
        if (blocker !== null || createSale.isPending) return;
        setSaleError(null);
        const request = buildAccessorySaleRequest(summary, payments, identifiers);
        const slot = idempotencySlotFor(idempotency.current, request, () => crypto.randomUUID());
        idempotency.current = slot;
        try {
            const sale = await createSale.mutateAsync({ request, idempotencyKey: slot.key });
            idempotency.current = null;
            dispatch({ type: 'clear' });
            setPayments(initialPaymentDrafts());
            setIdentifiers({});
            setCartOpen(false);
            setReceipt(sale);
        } catch (error) {
            const failure = saleFailure(error);
            setSaleError(failure.message);
            toast.error(failure.message);
            recoverFrom(failure);
        }
    };

    // La confirmación es modal: el cajón se cierra para no apilarlos.
    const fromDrawer = (open: () => void) => () => {
        setCartOpen(false);
        open();
    };

    return (
        <ScreenFrame>
            <div className="flex min-h-0 flex-1 flex-col">
                <PosShell
                    filtersCollapsed={pos.filtersCollapsed}
                    filters={
                        <PosFiltersPanel
                            filters={pos.filters}
                            options={{ ...pos.options, tiers: [], capacities: [] }}
                            onChange={pos.setFilters}
                            onSubmitSearch={() => {
                                const first = pos.rows.at(0);
                                if (first) changeQty(first.sku, 1);
                            }}
                            searchRef={pos.searchRef}
                            searchLabel="Buscar producto o servicio"
                            collapsed={pos.filtersCollapsed}
                            onToggleCollapsed={pos.toggleFiltersCollapsed}
                        />
                    }
                >
                    <AccessoriesTable
                        rows={pos.rows}
                        stats={pos.stats}
                        stat={pos.stat}
                        onStatChange={pos.setStat}
                        query={pos.filters.query}
                        sort={pos.sort}
                        onSortToggle={pos.toggleSort}
                        cart={cart}
                        onChangeQty={changeQty}
                        isLoading={catalog.isLoading}
                        isError={catalog.isError}
                        onRetry={() => {
                            void catalog.refetch();
                        }}
                    />
                </PosShell>
                <AccessoriesCartBar
                    summary={summary}
                    onOpen={() => {
                        setCartOpen(true);
                    }}
                />
            </div>

            <AccessoriesCartDrawer
                open={cartOpen && summary.unitsCount > 0}
                onClose={() => {
                    setCartOpen(false);
                }}
                summary={summary}
                onChangeQty={changeQty}
                identifiers={identifiers}
                onIdentifiersChange={(sku, next) => {
                    setIdentifiers((prev) => ({ ...prev, [sku]: next }));
                }}
                onRemove={(sku) => {
                    dispatch({ type: 'remove', sku });
                    setIdentifiers(({ [sku]: _removed, ...rest }) => rest);
                }}
                onClear={fromDrawer(() => {
                    setConfirmingClear(true);
                })}
                payments={payments}
                onPaymentsChange={setPayments}
                blocker={blocker}
                onCharge={() => void charge()}
                charging={createSale.isPending}
                saleError={saleError}
            />

            <ConfirmDialog
                open={confirmingClear}
                onOpenChange={setConfirmingClear}
                title="¿Vaciar el carrito?"
                confirmLabel="Vaciar"
                destructive
                onConfirm={() => {
                    dispatch({ type: 'clear' });
                    setIdentifiers({});
                    setConfirmingClear(false);
                }}
            />

            <AccessorySaleReceiptModal
                sale={receipt}
                onClose={() => {
                    setReceipt(null);
                }}
            />
        </ScreenFrame>
    );
}
